// Label languages (Settings → Languages): the organisation's main language (English) and its additional label languages
// (e.g. Arabic), added by an Admin without a new build. Translations are typed or pasted by people (stored exactly as
// entered, any script) — never machine-translated. They are kept per language, keyed by what they translate:
// "colour:Ocean Blue", "fibre:Cotton", "product:ARC014", "caption:PRODUCT", "phrase:madeIn" …
export interface LabelLanguage { code: string; name: string; rtl: boolean }
export interface LanguageSettings { main: LabelLanguage; extra: LabelLanguage[] }
export type Translations = Record<string, Record<string, string>>;   // language code → key → text

/** Languages offered when adding one (any other can be added by code and name). */
export const KNOWN_LANGUAGES: LabelLanguage[] = [
  { code: "ar", name: "Arabic", rtl: true }, { code: "ur", name: "Urdu", rtl: true }, { code: "fa", name: "Persian", rtl: true }, { code: "he", name: "Hebrew", rtl: true },
  { code: "fr", name: "French", rtl: false }, { code: "de", name: "German", rtl: false }, { code: "es", name: "Spanish", rtl: false }, { code: "it", name: "Italian", rtl: false },
  { code: "nl", name: "Dutch", rtl: false }, { code: "pt", name: "Portuguese", rtl: false }, { code: "pl", name: "Polish", rtl: false }, { code: "tr", name: "Turkish", rtl: false },
  { code: "ru", name: "Russian", rtl: false }, { code: "zh", name: "Chinese", rtl: false }, { code: "hi", name: "Hindi", rtl: false }, { code: "bn", name: "Bengali", rtl: false },
];
export const ENGLISH: LabelLanguage = { code: "en", name: "English", rtl: false };
export const DEFAULT_LANGUAGES: LanguageSettings = { main: ENGLISH, extra: [] };

const code = (v: unknown) => String(v ?? "").trim().toLowerCase().replace(/[^a-z-]/g, "").slice(0, 10);
export function normalizeLanguage(v: any): LabelLanguage | null {
  const c = code(v?.code);
  if (!c) return null;
  const known = KNOWN_LANGUAGES.find((k) => k.code === c);
  const name = String(v?.name ?? "").trim().slice(0, 40) || known?.name || c;
  return { code: c, name, rtl: typeof v?.rtl === "boolean" ? v.rtl : !!known?.rtl };
}
export function normalizeLanguages(v: any): LanguageSettings {
  const main = normalizeLanguage(v?.main) || ENGLISH;
  const extra: LabelLanguage[] = [];
  for (const x of Array.isArray(v?.extra) ? v.extra : []) {
    const l = normalizeLanguage(x);
    if (l && l.code !== main.code && !extra.some((e) => e.code === l.code)) extra.push(l);
  }
  return { main, extra: extra.slice(0, 10) };
}
export function normalizeTranslations(v: any): Translations {
  const out: Translations = {};
  for (const [lang, map] of Object.entries(v && typeof v === "object" ? v : {})) {
    const c = code(lang);
    if (!c || !map || typeof map !== "object") continue;
    const m: Record<string, string> = {};
    for (const [k, t] of Object.entries(map as Record<string, unknown>)) {
      const key = String(k).slice(0, 300), text = String(t ?? "");
      if (key && text.trim()) m[key] = text.slice(0, 1000);
    }
    out[c] = m;
  }
  return out;
}

/** Right-to-left scripts: Hebrew, Arabic (with Syriac, Thaana, N'Ko) and their presentation forms. */
export const RTL_TEXT = /[֐-ࣿיִ-﷿ﹰ-﻿]/;
export const isRtlText = (s: string) => RTL_TEXT.test(s);
/** Anything outside Latin, so exports embed the fonts that print it. */
export const needsEmbeddedFont = (s: string) => /[^\u0000-ɏ -⁯₠-⃏℀-⅏°·×–—…]/.test(s);

/** What can be translated, as the Settings screen groups it. */
export const TRANSLATION_TYPES: Array<[string, string]> = [
  ["product", "Product names"], ["category", "Product types / categories"], ["colour", "Colours"], ["fibre", "Fibres (composition)"],
  ["feature", "Features"], ["washCare", "Wash care wording"], ["standard", "Standard names"], ["country", "Countries (Made in …)"],
  ["company", "Company name"], ["brand", "Brand names"], ["tagline", "Brand taglines"], ["caption", "Label captions"], ["phrase", "Label phrases"],
];
export const TRANSLATION_TYPE_LABEL = Object.fromEntries(TRANSLATION_TYPES) as Record<string, string>;
export const tKey = (type: string, source: string) => `${type}:${String(source ?? "").trim()}`;

/** Captions printed on carton marks, carton labels and stickers (English, then each language's translation). */
export const LABEL_CAPTIONS = ["PRODUCT", "COLOUR", "COMPOSITION", "STYLE", "SIZE", "QTY", "SIZE · QTY", "G.W.", "N.W.", "COUNTRY OF ORIGIN", "CARTON NO", "ORDER", "BATCH", "OF", "Size", "Colour", "Batch"];
/** Phrases with a place for a value, e.g. "Made in {country}". */
export const LABEL_PHRASES: Record<string, string> = { madeIn: "Made in {country}" };

/** How a label prints its languages. */
export type LangMode = "main" | "second" | "both";
export type LangLayout = "side" | "stacked";
export const LANG_MODES: Array<[LangMode, string]> = [["main", "Main language only"], ["second", "Second language only"], ["both", "Bilingual"]];
export const LANG_LAYOUTS: Array<[LangLayout, string]> = [["stacked", "Stacked (second language under the first)"], ["side", "Side by side (adjacent cells)"]];

/** The translated text for one value, or "" when missing. */
export const translate = (t: Translations, lang: string, type: string, source: string) => (t[lang] || {})[tKey(type, source)] || "";
/** The plain-language warning for a missing translation, e.g. "Arabic missing for Ocean Blue". */
export const missingText = (langName: string, what: string) => `${langName} missing for ${what}`;
